// A small SVG pie (no chart library): one wedge per slice, clockwise from 12 o'clock in the order
// given, in the line and bar charts' series colours, with a legend of each slice's value and
// share. Each wedge carries a native <title> tooltip; the SVG is one labelled image to assistive
// technology, and the legend (plain text, not hidden) carries the same numbers.
// Geometry: ui/chart-model.ts.
import { arcPath, pieAngles, seriesClass, sharePercent } from "./chart-model";

export interface PieSlice {
  id: string;
  label: string;
  value: number;
}

export interface PieChartProps {
  slices: readonly PieSlice[];
  /** One sentence naming what the pie shows, for screen readers; the shares are appended. */
  label?: string | undefined;
  formatValue?: ((value: number) => string) | undefined;
  class?: string | undefined;
}

const SIZE = 160;
const RADIUS = 76;

export function PieChart({ slices, label = "Share", formatValue = (value) => value.toLocaleString("en-US"), class: className }: PieChartProps) {
  const angles = pieAngles(slices.map((slice) => slice.value));
  const shares = slices.map((slice, index) => ({ ...slice, share: sharePercent(angles[index]?.fraction ?? 0), angle: angles[index] }));
  const summary = `${label}: ${shares.map((s) => `${s.label} ${s.share}`).join(", ") || "nothing to show"}.`;

  return (
    <div class={className ? `chart pie-chart ${className}` : "chart pie-chart"}>
      <svg class="pie-svg" viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE} role="img" aria-label={summary}>
        {shares.map((slice, index) => {
          const d = slice.angle ? arcPath(SIZE / 2, SIZE / 2, RADIUS, slice.angle.start, slice.angle.end) : "";
          return d ? (
            <path key={slice.id} class={`pie-slice ${seriesClass(slice.id, index)}`} d={d}>
              <title>{`${slice.label}: ${formatValue(slice.value)} (${slice.share})`}</title>
            </path>
          ) : null;
        })}
      </svg>
      <ul class="chart-legend pie-legend">
        {shares.map((slice, index) => (
          <li key={slice.id}>
            <svg class="legend-swatch" viewBox="0 0 10 10" aria-hidden="true">
              <rect class={seriesClass(slice.id, index)} x="0" y="0" width="10" height="10" rx="2" />
            </svg>
            <span class="pie-legend-label">{slice.label}</span>
            <span class="pie-legend-value">
              {formatValue(slice.value)} · {slice.share}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
